import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import Apple from "@/app/assets/icons/apple";

const BASE_BUTTON_CLASSES =
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full  focus-visible-active:ring-2 focus-visible-active:ring-neutralOpacityLight-8 text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none active:ring-2 active:ring-neutralOpacityDark-8 focus-visible:ring-2 focus-visible:ring-black disabled:pointer-events-none  ";

export enum ButtonVariant {
    PrimaryDefault = "primary-default",
    PrimaryWhite = "primary-white",
    SecondaryDefault = "secondary-default",
    SecondaryWhite = "secondary-white",
    TertiaryDefault = "tertiary-default",
    TertiaryWhite = "tertiary-white",
}

export enum ButtonSize {
    Small = "sm",
    Medium = "md",
    Large = "lg",
    Extrasmall = "xs",
}

const VARIANT_STYLES = {
    [ButtonVariant.PrimaryDefault]:
        "bg-brand-primary-500  text-white  hover:bg-brand-primary-600 focus:bg-brand-primary-500 disabled:bg-neutralOpacityDark-8 disabled:text-neutralOpacityDark-30",
    [ButtonVariant.PrimaryWhite]:
        "bg-white  text-black  hover:bg-white hover:bg-neutralOpacityLight-95  disabled:bg-neutralOpacityLight-20 disabled:text-neutralOpacityLight-30",
    [ButtonVariant.SecondaryDefault]:
        "bg-transparent text-black  hover:bg-white border border-neutralOpacityDark-30 disabled:text-neutralOpacityDark-30 disabled:border-neutralOpacityDark-8",
    [ButtonVariant.SecondaryWhite]:
        "bg-transparent text-white hover:bg-neutralOpacityLight-50 border border-neutralOpacityLight-30 disabled:text-neutralOpacityLight-40 disabled:border-neutralOpacityLight-40",
    [ButtonVariant.TertiaryDefault]:
        "bg-transparent text-black  hover:bg-neutralOpacityDark-8  focus:bg-neutralOpacityDark-8 disabled:text-neutralOpacityDark-30",
    [ButtonVariant.TertiaryWhite]:
        "bg-transparent text-white  hover:bg-neutralOpacityLight-20  focus:bg-neutralOpacityLight-20 disabled:text-neutralOpacityLight-40",
};

const IMAGE_STYLES = {
    [ButtonVariant.PrimaryDefault]: "text-white ",
    [ButtonVariant.PrimaryWhite]: "",
    [ButtonVariant.SecondaryDefault]: "",
    [ButtonVariant.SecondaryWhite]: "",
    [ButtonVariant.TertiaryDefault]: "",
    [ButtonVariant.TertiaryWhite]: "",
};

const DISABLED_IMAGE_STYLES = {
    [ButtonVariant.PrimaryDefault]: " text-neutralOpacityDark-40 ",
    [ButtonVariant.PrimaryWhite]: " text-neutralOpacityLight-30 ",
    [ButtonVariant.SecondaryDefault]: "text-neutralOpacityDark-40",
    [ButtonVariant.SecondaryWhite]: "text-neutralOpacityLight-30 ",
    [ButtonVariant.TertiaryDefault]: "text-neutralOpacityDark-40",
    [ButtonVariant.TertiaryWhite]: "text-neutralOpacityLight-30 ",
};

const SIZE_STYLES = {
    [ButtonSize.Small]: "min-w-s12 min-h-s12 max-w-fit",
    [ButtonSize.Medium]: "min-w-s14 min-h-s14 max-w-fit",
    [ButtonSize.Large]: "min-w-s15 min-h-s15 max-w-fit ",
    [ButtonSize.Extrasmall]: "min-w-s9 min-h-s9 max-w-fit ",
};

const IMAGE_SIZE_STYLES = {
    [ButtonSize.Extrasmall]: "h-s6 w-s6",
    [ButtonSize.Small]: "h-s7 w-s7",
    [ButtonSize.Medium]: "h-s9 w-s9",
    [ButtonSize.Large]: "h-s11 w-s11",
};

const buttonVariants = cva(BASE_BUTTON_CLASSES, {
    variants: {
        variant: VARIANT_STYLES,
        size: SIZE_STYLES,
    },
    defaultVariants: {
        variant: ButtonVariant.PrimaryDefault,
        size: ButtonSize.Small,
    },
});

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
    icon?: React.ReactElement;
    label?: string;
    variant?: ButtonVariant;
    size?: ButtonSize;
}

const IconButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant,
            size,
            asChild = false,
            disabled,
            icon = <Apple />,
            ...props
        },
        ref,
    ) => {
        const Comp = asChild ? Slot : "button";
        const imageStyle = variant ? IMAGE_STYLES[variant] : "";
        const imageStyleSize = size ? IMAGE_SIZE_STYLES[size] : "";
        const disabledImageStyle = variant && disabled ? DISABLED_IMAGE_STYLES[variant] : "";

        const styledIcon = icon ? (
            React.cloneElement(icon, {
                className: cn(
                    icon.props.className,
                    imageStyleSize, // Add size styling
                    disabled ? disabledImageStyle : imageStyle, // Conditionally add styles
                ),
            })
        ) : (
            <></>
        );

        return (
            <Comp
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                disabled={disabled}
                {...props}
            >
                {styledIcon}
            </Comp>
        );
    },
);

IconButton.displayName = "Button";

export { IconButton };
